import StyledField from "common/TextField/TexField1";
import {
  Box,
  Button,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Filter from "common/Select/SelectMultiFilter";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import DynamicSelect from "common/Select/Select";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import { BarcodeScanner } from "react-barcode-scanner";
import { BarcodeDetectorPolyfill } from "@preflower/barcode-detector-polyfill";
import "react-barcode-scanner/polyfill";
import FormDialog from "common/Modal/Modal";
import { useRef, useState } from "react";
import { useSnackbar } from "notistack";

const BARCODE_FORMATS = [
  "code_128",
  "code_39",
  "code_93",
  "codabar",
  "ean_13",
  "ean_8",
  "itf",
  "upc_a",
  "upc_e",
];

const detectBarcodeFromImage = async (img: HTMLImageElement) => {
  const detector = new BarcodeDetectorPolyfill({ formats: BARCODE_FORMATS });
  for (const scale of [3, 2, 4, 1]) {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth * scale;
    canvas.height = img.naturalHeight * scale;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const barcodes = await detector.detect(canvas);
    if (barcodes.length) return barcodes;
  }
  return [];
};

interface ItemSearchHeaderComponentProps {
  handleChangeFilter: (event: any) => void;
  handleChangeSearch: (field: string, value: any) => void;
  handleSubmitSearch: () => void;
  searchFormData: any;
  filter: string[];
  clearSearch: () => void;
  search: any;
  categories: any;
}

const ItemSearchHeader: React.FC<ItemSearchHeaderComponentProps> = ({
  handleChangeFilter,
  handleChangeSearch,
  handleSubmitSearch,
  searchFormData,
  filter,
  clearSearch,
  search,
  categories,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const filterOptions = ["name", "itemCode", "category"];
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  const [barcodeCameraFacing, setBarcodeCameraFacing] = useState<
    "environment" | "user"
  >("user");
  const barcodeImageRef = useRef<HTMLInputElement>(null);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSubmitSearch();
    }
  };

  const applyBarcode = (value: string) => {
    handleChangeSearch("itemCode", value);
    setBarcodeScannerOpen(false);
  };

  return (
    <>
      <Grid container spacing={1} alignItems="flex-start">
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <Filter
            label="Filter By"
            filterOptions={filterOptions}
            handleChangeFilter={handleChangeFilter}
            filter={filter}
          />
        </Grid>
        {filter.map((fieldName) =>
          fieldName === "category" ? (
            <Grid item key={fieldName} xs={12} sm={6} md={3}>
              <DynamicSelect
                value={categories.find(
                  (option: any) => option.value === searchFormData?.category,
                )}
                isDisabled={false}
                isLoading={false}
                isClearable={true}
                isRtl={false}
                isSearchable={true}
                name="category"
                options={categories}
                isRequired={true}
                onChange={(e) => handleChangeSearch("category", e)}
                label=""
                onKeyDown={handleKeyPress}
                defaultPlaceholder={t("category")}
              />
            </Grid>
          ) : (
            <Grid item key={fieldName} xs={12} sm={6} md={3}>
              <StyledField
                fullWidth={true}
                placeholder={t(fieldName)}
                displayPlaceholder={true}
                handleChange={(e) =>
                  handleChangeSearch(fieldName, e.target.value)
                }
                required={true}
                value={searchFormData?.[fieldName] || ""}
                handleKeyDown={handleKeyPress}
                endIcon={
                  fieldName === "itemCode" ? (
                    <DocumentScannerOutlinedIcon
                      className="cursor-pointer"
                      sx={{
                        color: themeColors?.primary,
                        fontSize: 19,
                        marginRight: "10px",
                      }}
                      onClick={() => setBarcodeScannerOpen(true)}
                    />
                  ) : undefined
                }
              />
            </Grid>
          ),
        )}
        <Grid
          item
          xs={12}
          sm={6}
          md={2}
          sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
        >
          <Button
            variant="contained"
            disabled={filter.length ? false : true}
            onClick={handleSubmitSearch}
            sx={{
              boxShadow: "none",
              background: filter.length > 0 ? themeColors?.primary : "#B8C0C6",
              height: 38,
              minHeight: 38,
              py: 0,
              px: 3,
            }}
            className={`text-white text-ellipsis overflow-hidden font-Saira font-[600] text-[13px]`}
          >
            {t("submit")}
          </Button>
          {(filter.length && Object.keys(search).length) ||
          filter.length > 1 ? (
            <Button
              variant="contained"
              onClick={clearSearch}
              sx={{
                boxShadow: "none",
                color: themeColors?.primary,
                background: "#d8d1e0",
                height: 38,
                minHeight: 38,
                py: 0,
                px: 3,
              }}
              className={`text-ellipsis overflow-hidden font-Saira font-[600] text-[13px]`}
            >
              {t("clear")}
            </Button>
          ) : null}
        </Grid>
      </Grid>
      {barcodeScannerOpen && (
        <FormDialog
          open={barcodeScannerOpen}
          onClose={() => {
            setBarcodeScannerOpen(false);
            setBarcodeCameraFacing("user");
          }}
          title={t("scanBarcode")}
          hideActions
          fullWidth
          maxWidth="sm"
          content={
            <Box>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={barcodeCameraFacing}
                onChange={(_, value) => {
                  if (value) setBarcodeCameraFacing(value);
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="environment">
                  {t("backCamera")}
                </ToggleButton>
                <ToggleButton value="user">{t("frontCamera")}</ToggleButton>
              </ToggleButtonGroup>
              <Box
                sx={{ height: 300, overflow: "hidden", borderRadius: 1, mb: 2 }}
              >
                <BarcodeScanner
                  key={barcodeCameraFacing}
                  trackConstraints={{
                    width: { min: 640, ideal: 1280 },
                    height: { min: 480, ideal: 720 },
                    facingMode: { exact: barcodeCameraFacing },
                  }}
                  options={{ delay: 300, formats: BARCODE_FORMATS }}
                  onCapture={(barcodes) => {
                    const value = barcodes[0]?.rawValue;
                    if (value) applyBarcode(value);
                  }}
                />
              </Box>
              <input
                ref={barcodeImageRef}
                type="file"
                accept="image/*"
                hidden
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  try {
                    const img = document.createElement("img");
                    const url = URL.createObjectURL(file);
                    await new Promise<void>((resolve, reject) => {
                      img.onload = () => resolve();
                      img.onerror = reject;
                      img.src = url;
                    });
                    const barcodes = await detectBarcodeFromImage(img);
                    URL.revokeObjectURL(url);
                    const value = barcodes[0]?.rawValue;
                    if (value) applyBarcode(value);
                    else
                      enqueueSnackbar(t("Not Found"), { variant: "warning" });
                  } catch {
                    enqueueSnackbar(t("Not Found"), { variant: "warning" });
                  }
                }}
              />
              <Button
                variant="outlined"
                fullWidth
                onClick={() => barcodeImageRef.current?.click()}
                sx={{
                  backgroundColor: themeColors?.primary,
                  color: "#fff",
                  borderColor: themeColors?.primary,
                  "&:hover": {
                    backgroundColor: themeColors?.primary,
                    borderColor: themeColors?.primary,
                    color: "#fff",
                  },
                }}
              >
                {t("upload")}
              </Button>
            </Box>
          }
        />
      )}
    </>
  );
};

export default ItemSearchHeader;
